import { Pipe, PipeTransform } from '@angular/core';

import { EnumTypeName, LocalizableEnumType } from '../helpers/enum-localization.helper';
import { LocalizedEnumPipe } from './localized-enum.pipe';

/**
 * Represents a lookup item with id and localized name
 */
export interface LookupItem<T extends string = string> {
  id: T;
  name: string;
}

/**
 * Pipe that transforms an enum object into an array of lookup items with localized names.
 * Useful for populating dropdowns and select lists.
 *
 * The enum name is automatically inferred from the enum type through TypeScript's type system.
 *
 * @example
 * ```html
 * @for (item of GenderV3EnumDto | localizedEnumToLookupItems: 'GenderV3EnumDto'; track item.id) {
 *   <ga-option [value]="item.id">{{ item.name }}</ga-option>
 * }
 * ```
 */
@Pipe({
  name: 'localizedEnumToLookupItems',
  standalone: true,
})
export class LocalizedEnumToLookupItemsPipe implements PipeTransform {
  private readonly localizedEnumPipe = new LocalizedEnumPipe();

  /**
   * Transforms an enum object to an array of lookup items with localized names.
   * @param enumObject - The enum object to transform (must be a TypeScript enum)
   * @param enumName - The type name of the enum (used for localization)
   * @returns Array of lookup items with id and localized name
   */
  transform<T extends Record<string, string>>(
    enumObject: T,
    enumName: EnumTypeName,
  ): LookupItem<T[keyof T]>[] {
    return (Object.values(enumObject) as T[keyof T][]).map((enumId) => ({
      id: enumId,
      name: this.localizedEnumPipe.transform(enumId as LocalizableEnumType, enumName),
    }));
  }
}
